import ReactDOM from "react-dom";
import React, { useMemo, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { HorizontalBar as ReactChartjs2HorizontalBar } from "react-chartjs-2";
import MUITypography from "@mui/material/Typography";
import MUIBox from "@mui/material/Box";

import Format from "../../../Utility/Format";

import Setting from "../../../Utility/Setting";
import Widget from "../../Widget";


/**
 * Reads the first defined numeric field from a statistics record, trying alternate API spellings.
 *
 * @param {object|null|undefined} activityStatisticsRecord Raw user statistics from the widget payload.
 * @param {string[]} fieldNames Candidate property names in preference order.
 * @returns {number}
 */
function readNumericActivityStatisticsField(activityStatisticsRecord, fieldNames) {
    if (activityStatisticsRecord === null || activityStatisticsRecord === undefined) {
        return 0;
    }
    for (let index = 0; index < fieldNames.length; index += 1) {
        const fieldName = fieldNames[index];
        const rawValue = activityStatisticsRecord[fieldName];
        if (rawValue === null || rawValue === undefined) {
            continue;
        }
        const numericValue = Number(rawValue);
        if (Number.isNaN(numericValue) === false) {
            return numericValue;
        }
    }
    return 0;
}

/**
 * Reads call and appointment statistics from `widgetData.users` when that map contains exactly one user entry.
 *
 * @param {object|null|undefined} widgetData Loaded widget response data.
 * @returns {{ completedCallsCount: number, scheduledCallsCount: number, completedAppointmentsCount: number, scheduledAppointmentsCount: number }|null} Totals for the sole user, or null when missing or when more than one user is present.
 */
function collectActivitiesStatisticsTotalsFromWidgetData(widgetData) {
    if (widgetData?.users === null || widgetData?.users === undefined) {
        return null;
    }

    if (Object.keys(widgetData.users).length === 0) {
        return null;
    }

    if (Object.keys(widgetData.users).length !== 1) {
        return null;
    }

    const activityStatisticsRecord = widgetData.users[Object.keys(widgetData.users)[0]];

    const completedCallsCount = readNumericActivityStatisticsField(activityStatisticsRecord, ["completedCalls"]);
    const scheduledCallsCount = readNumericActivityStatisticsField(activityStatisticsRecord, ["scheduledCalls"]);
    const completedAppointmentsCount = readNumericActivityStatisticsField(activityStatisticsRecord, [
        "completedAppointments",
        "completedAppoints",
    ]);
    const scheduledAppointmentsCount = readNumericActivityStatisticsField(activityStatisticsRecord, [
        "scheduledAppointments",
        "scheduledAppointsments",
    ]);

    return {
        completedCallsCount,
        scheduledCallsCount,
        completedAppointmentsCount,
        scheduledAppointmentsCount,
    };
}


/**
 * Single stacked horizontal bar: completed count, scheduled count, and remainder toward the chart maximum.
 *
 * @param {object} props
 * @param {number} props.completedCount Completed segment length.
 * @param {number} props.scheduledCount Scheduled segment length.
 * @param {number} props.targetCount Reference target shown as a dashed vertical marker.
 * @param {number} props.chartScaleMaximumCount Horizontal axis maximum.
 * @param {string} props.captionLabel Short label shown beside the bar (for example Calls).
 * @param {string} props.targetDescriptionText Target line shown only in the chart tooltip. When the text begins with `Target ` followed by detail text, the word Target is shown on the left and the remainder on the right (for example Target 50 / week).
 * @returns {React.ReactElement}
 */
function WidgetDashboardActivitiesStackedBarRow({
    completedCount,
    scheduledCount,
    targetCount,
    chartScaleMaximumCount,
    captionLabel,
    targetDescriptionText,
}) {
    const theme = useTheme();
    const chartContainerElementReference = useRef(null);
    const targetCountReference = useRef(targetCount);
    const targetLineColorReference = useRef(theme.palette.text.secondary);

    const [activitiesChartTooltipState, setActivitiesChartTooltipState] = useState({
        isVisible: false,
        anchorLeftPixels: 0,
        anchorTopPixels: 0,
    });

    targetCountReference.current = targetCount;
    targetLineColorReference.current = theme.palette.text.secondary;


    const remainingCountToFillChartScale = Math.max(
        chartScaleMaximumCount - completedCount - scheduledCount,
        0
    );

    const activitiesTooltipTargetTitleSegments = useMemo(() => {
        const trimmedTargetDescriptionText = String(
            targetDescriptionText === undefined || targetDescriptionText === null
                ? ""
                : targetDescriptionText
        ).trim();
        if (trimmedTargetDescriptionText.length === 0) {
            return {
                shouldShowTooltipTargetTitleRow: false,
                tooltipTargetLeftLabelText: "",
                tooltipTargetRightDetailText: "",
                usesSplitTargetTooltipTitleLayout: false,
            };
        }
        const targetWordFollowedByDetailPattern = /^Target\s+(.+)$/;
        const patternMatchResult = trimmedTargetDescriptionText.match(targetWordFollowedByDetailPattern);
        if (patternMatchResult !== null && patternMatchResult[1] !== undefined) {
            return {
                shouldShowTooltipTargetTitleRow: true,
                tooltipTargetLeftLabelText: "Target",
                tooltipTargetRightDetailText: patternMatchResult[1].trim(),
                usesSplitTargetTooltipTitleLayout: true,
            };
        }
        return {
            shouldShowTooltipTargetTitleRow: true,
            tooltipTargetLeftLabelText: "",
            tooltipTargetRightDetailText: trimmedTargetDescriptionText,
            usesSplitTargetTooltipTitleLayout: false,
        };
    }, [targetDescriptionText]);

    const activitiesBarChartData = useMemo(() => {
        const remainingBarBackgroundColor = theme.palette.background.default;

        const sharedDatasetBarSizing = {
            barPercentage: 1,
            categoryPercentage: 0.65,
        };

        return {
            labels: [""],
            datasets: [
                {
                    label: "Completed",
                    data: [completedCount],
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    borderWidth: 0,
                    stack: "activities",
                    ...sharedDatasetBarSizing,
                },
                {
                    label: "Scheduled",
                    data: [scheduledCount],
                    backgroundColor: theme.palette.secondary.main,
                    borderColor: theme.palette.secondary.main,
                    borderWidth: 0,
                    stack: "activities",
                    ...sharedDatasetBarSizing,
                },
                {
                    label: "Remaining",
                    data: [remainingCountToFillChartScale],
                    backgroundColor: remainingBarBackgroundColor,
                    borderColor: remainingBarBackgroundColor,
                    borderWidth: 0,
                    stack: "activities",
                    ...sharedDatasetBarSizing,
                },
            ],
        };
    }, [
        completedCount,
        scheduledCount,
        remainingCountToFillChartScale,
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme,
    ]);


    const activitiesBarChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            legend: { display: false },
            layout: { padding: { top: 2, right: 6, bottom: 2, left: 2 } },
            hover: {
                mode: "index",
                axis: "y",
                intersect: false,
            },
            scales: {
                xAxes: [
                    {
                        stacked: true,
                        display: false,
                        gridLines: { display: false, drawBorder: false },
                        ticks: {
                            display: false,
                            beginAtZero: true,
                            min: 0,
                            max: chartScaleMaximumCount,
                            suggestedMin: 0,
                            suggestedMax: chartScaleMaximumCount,
                        },
                    },
                ],
                yAxes: [
                    {
                        stacked: true,
                        display: false,
                        gridLines: { display: false, drawBorder: false },
                        ticks: { display: false },
                    },
                ],
            },
            tooltips: {
                enabled: false,
                mode: "index",
                intersect: false,
                custom(chartTooltipModel) {
                    const chartContainerElement = chartContainerElementReference.current;
                    if (chartContainerElement === null || chartContainerElement === undefined) {
                        return;
                    }

                    if (chartTooltipModel.opacity === 0) {
                        setActivitiesChartTooltipState((previousTooltipState) => {
                            if (previousTooltipState.isVisible === false) {
                                return previousTooltipState;
                            }
                            return { ...previousTooltipState, isVisible: false };
                        });
                        return;
                    }

                    const chartContainerBoundingRectangle = chartContainerElement.getBoundingClientRect();

                    const chartHorizontalCenterPixelX =
                        chartContainerBoundingRectangle.left +
                        chartContainerBoundingRectangle.width / 2;
                    const chartBottomPixelY = chartContainerBoundingRectangle.bottom;

                    setActivitiesChartTooltipState((previousTooltipState) => {
                        if (
                            previousTooltipState.isVisible === true &&
                            previousTooltipState.anchorLeftPixels === chartHorizontalCenterPixelX &&
                            previousTooltipState.anchorTopPixels === chartBottomPixelY
                        ) {
                            return previousTooltipState;
                        }
                        return {
                            isVisible: true,
                            anchorLeftPixels: chartHorizontalCenterPixelX,
                            anchorTopPixels: chartBottomPixelY,
                        };
                    });
                },
            },
        }),
        [chartScaleMaximumCount]
    );

    const handleActivitiesChartContainerPointerLeave = () => {
        setActivitiesChartTooltipState((previousTooltipState) => {
            if (previousTooltipState.isVisible === false) {
                return previousTooltipState;
            }
            return { ...previousTooltipState, isVisible: false };
        });
    };

    return (
        <>
            <MUIBox
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                    mt: 1,
                    width: "100%",
                }}
            >
                <MUITypography
                    color="text.secondary"
                    component="div"
                    sx={{ fontSize: "0.7rem", lineHeight: 0 }}
                    variant="caption"
                >
                    {captionLabel}
                </MUITypography>
                <MUIBox
                    ref={chartContainerElementReference}
                    onPointerLeave={handleActivitiesChartContainerPointerLeave}
                    sx={{
                        width: "100%",
                        height: 20,
                        minHeight: 20,
                        flexShrink: 0,
                        position: "relative",
                    }}
                >
                    <ReactChartjs2HorizontalBar
                        data={activitiesBarChartData}
                        options={activitiesBarChartOptions}
                    />
                </MUIBox>
            </MUIBox>

            {activitiesChartTooltipState.isVisible === true &&
                ReactDOM.createPortal(
                    <MUIBox
                        sx={{
                            position: "fixed",
                            left: `${activitiesChartTooltipState.anchorLeftPixels}px`,
                            top: `${activitiesChartTooltipState.anchorTopPixels}px`,
                            transform: "translate(-50%, 8px)",
                            pointerEvents: "none",
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            padding: "8px 10px",
                            borderRadius: 1,
                            fontSize: "0.9rem",
                            lineHeight: 1.3,
                            whiteSpace: "nowrap",
                            zIndex: theme.zIndex.tooltip,
                            boxShadow: 4,
                            minWidth: 150,
                        }}
                    >
                        {activitiesTooltipTargetTitleSegments.shouldShowTooltipTargetTitleRow === true && (
                            <>
                                <MUIBox
                                    sx={{
                                        alignItems: "center",
                                        display: "flex",
                                        justifyContent:
                                            activitiesTooltipTargetTitleSegments
                                                .usesSplitTargetTooltipTitleLayout === true
                                                ? "space-between"
                                                : "flex-start",
                                        gap: 2,
                                        marginBottom: "4px",
                                        width: "100%",
                                    }}
                                >
                                    {activitiesTooltipTargetTitleSegments
                                        .usesSplitTargetTooltipTitleLayout === true ? (
                                        <>
                                            <MUIBox
                                                component="span"
                                                sx={{
                                                    color: "text.primary",
                                                    flexShrink: 0,
                                                    fontSize: "0.9rem",
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {
                                                    activitiesTooltipTargetTitleSegments
                                                        .tooltipTargetLeftLabelText
                                                }
                                            </MUIBox>
                                            <MUIBox
                                                component="span"
                                                sx={{
                                                    color: "text.primary",
                                                    flexShrink: 0,
                                                    fontSize: "0.9rem",
                                                    fontVariantNumeric: "tabular-nums",
                                                    textAlign: "right",
                                                }}
                                            >
                                                {
                                                    activitiesTooltipTargetTitleSegments
                                                        .tooltipTargetRightDetailText
                                                }
                                            </MUIBox>
                                        </>
                                    ) : (
                                        <MUIBox
                                            component="span"
                                            sx={{
                                                color: "text.primary",
                                                fontSize: "0.9rem",
                                            }}
                                        >
                                            {
                                                activitiesTooltipTargetTitleSegments
                                                    .tooltipTargetRightDetailText
                                            }
                                        </MUIBox>
                                    )}
                                </MUIBox>
                                <MUIBox
                                    sx={{
                                        height: "1px",
                                        backgroundColor: theme.palette.divider,
                                        marginTop: "2px",
                                        marginBottom: "6px",
                                    }}
                                />
                            </>
                        )}
                        <MUIBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {[
                                {
                                    key: "completed",
                                    label: "Completed",
                                    value: completedCount,
                                    color: theme.palette.primary.main,
                                    measurementUnitLabel: "",
                                },
                                {
                                    key: "scheduled",
                                    label: "Scheduled",
                                    value: scheduledCount,
                                    color: theme.palette.secondary.main,
                                    measurementUnitLabel: "",
                                },
                            ].map((tooltipProgressRow) => (
                                <MUIBox
                                    key={tooltipProgressRow.key}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <MUIBox
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            backgroundColor: tooltipProgressRow.color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <MUIBox
                                        component="span"
                                        sx={{ fontVariantNumeric: "tabular-nums" }}
                                    >
                                        {Format.number(tooltipProgressRow.value)}
                                        {tooltipProgressRow.measurementUnitLabel !== undefined &&
                                            tooltipProgressRow.measurementUnitLabel !== null &&
                                            String(tooltipProgressRow.measurementUnitLabel).length > 0
                                            ? ` ${tooltipProgressRow.measurementUnitLabel}`
                                            : ""}{" "}
                                        {tooltipProgressRow.label}
                                    </MUIBox>
                                </MUIBox>
                            ))}
                        </MUIBox>
                    </MUIBox>,
                    document.body
                )}
        </>
    );
}

/**
 * Call and in-service appointment progress bars for the activities dashboard widget.
 *
 * @param {object} props
 * @param {object|null|undefined} props.widgetData Loaded widget response data.
 * @returns {React.ReactElement}
 */
function WidgetDashboardActivitiesContent({ widgetData, dashboardWidget }) {
    const hasExactlyOneSalesRepresentativeQueuePrefilterSelection =
        Array.isArray(Setting.get("queue.prefilters.userIds")) === true &&
        Setting.get("queue.prefilters.userIds").length === 1

    const statisticsTotals = useMemo(() => {
        if (hasExactlyOneSalesRepresentativeQueuePrefilterSelection === false) {
            return null;
        }
        return collectActivitiesStatisticsTotalsFromWidgetData(widgetData);
    }, [widgetData, hasExactlyOneSalesRepresentativeQueuePrefilterSelection]);

    if (statisticsTotals === null) {
        const emptyStateMessageText =
            hasExactlyOneSalesRepresentativeQueuePrefilterSelection === false
                ? "Select exactly one sales representative to see call and in-service appointment progress."
                : "No call and in-service appointment statistics are available for the selected sales representative.";
        return (
            <MUITypography color="text.secondary" sx={{ fontSize: "0.8rem" }} variant="body2">
                {emptyStateMessageText}
            </MUITypography>
        );
    }

    const totalCallsCount =
        statisticsTotals.completedCallsCount + statisticsTotals.scheduledCallsCount;
    const chartScaleMaximumCallsCount = Math.max(
        JSON.parse(dashboardWidget.jsonConfiguration).callsTarget,
        totalCallsCount
    );

    const totalAppointmentsCount =
        statisticsTotals.completedAppointmentsCount + statisticsTotals.scheduledAppointmentsCount;
    const chartScaleMaximumAppointmentsCount = Math.max(
        JSON.parse(dashboardWidget.jsonConfiguration).appointmentsTarget,
        totalAppointmentsCount
    );

    return (
        <MUIBox
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                minWidth: 0,
            }}
        >
            <WidgetDashboardActivitiesStackedBarRow
                captionLabel="Calls"
                chartScaleMaximumCount={chartScaleMaximumCallsCount}
                completedCount={statisticsTotals.completedCallsCount}
                scheduledCount={statisticsTotals.scheduledCallsCount}
                targetCount={JSON.parse(dashboardWidget.jsonConfiguration).callsTarget}
                targetDescriptionText={`Target ${JSON.parse(dashboardWidget.jsonConfiguration).callsTarget} / week`}
            />
            <WidgetDashboardActivitiesStackedBarRow
                captionLabel={"Appointments"}
                chartScaleMaximumCount={chartScaleMaximumAppointmentsCount}
                completedCount={statisticsTotals.completedAppointmentsCount}
                scheduledCount={statisticsTotals.scheduledAppointmentsCount}
                targetCount={JSON.parse(dashboardWidget.jsonConfiguration).appointmentsTarget}
                targetDescriptionText={`Target ${JSON.parse(dashboardWidget.jsonConfiguration).appointmentsTarget} / month`}
            />
        </MUIBox>
    );
}

/**
 * Queue dashboard widget: completed versus scheduled calls and in-service appointments for the filtered user scope.
 *
 * @param {object} props
 * @param {number|string} props.widgetId Dashboard widget instance identifier used to load data.
 * @param {object} props.dashboardWidget Dashboard widget configuration including display name.
 * @param {boolean} props.isActive Whether this tile is the active dashboard widget.
 * @returns {React.ReactElement}
 */
export default function WidgetDashboardActivities(props) {
    return (
        <Widget
            getWidgetId={() => props.widgetId}
            getTitle={() => props.dashboardWidget.name}
            isActive={() => props.isActive}
            renderContent={({ widgetData }) => (
                <WidgetDashboardActivitiesContent
                    widgetData={widgetData}
                    dashboardWidget={props.dashboardWidget}
                />
            )}
        />
    );
}
